/** @jsx isolateComponent */
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import { Icon, IconName } from '../../display/Icon';
import { Link } from '../../navigation/Link';

export type ButtonColor =
  | 'danger'
  | 'primary'
  | 'primary-alt'
  | 'secondary'
  | 'tertiary'
  | 'icon-primary'
  | 'icon-alt'
  | 'icon-secondary';

export type Props = {
  /**
   * Optional reference to the `id` of an element that `Button` controls.
   * Often used in conjunction with `ariaExpanded`.
   */
  ariaControls?: string;
  /**
   * Optional `id` of element that describes `Button`.
   */
  ariaDescribedBy?: string;
  /**
   * Optional `aria-expanded` attribute to indicate whether `Button`
   * controls content that can be expanded or collapsed, and whether that
   * content is expanded.
   */
  ariaExpanded?: boolean;
  /**
   * Optional `aria-haspopup` attribute, indicating whether `Button` opens a popup.
   */
  ariaHaspopup?: boolean;
  /**
   * Optional `aria-hidden` attribute indicating whether `Button` should be
   * hidden from screenreaders. Only use in rare cases where accessibility is
   * improved, such as if `Button` is redundant in a particular context.
   */
  ariaHidden?: boolean;
  /**
   * Optional `aria-label` to describe `Button`.
   */
  ariaLabel?: string;
  /**
   * Content to render within `Button`. Function children are supported.
   */
  children: React.ReactNode;
  /**
   * The color style to render for `Button`.
   */
  color?: ButtonColor;
  /**
   * Optionally render `Button` as `Link`.
   */
  href?: string;
  /**
   * The `target` attribute for `Button` when it renders as a `Link`.
   */
  target?: '_self' | '_blank' | '_parent' | '_top';
  /**
   * The `rel` attribute for `Button` when it renders as a `Link`.
   */
  rel?: string;
  /**
   * Whether or not `Button` is disabled. If `href` is supplied and
   * `disabled` is `true`, `Button` will render as a `button` instead
   * of as a `Link`.
   */
  disabled?: boolean;
  /**
   * The `tabindex` attribute to apply to `Button`. Should only be used
   * in very specific circumstances.
   */
  tabIndex?: number;
  /**
   * The `type` attribute for `Button` when it renders as a `button`.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Optional `for` attribute to apply to node.
   * If supplied, the rendered tag will be a `label`.
   */
  htmlFor?: string;
  /**
   * Optional icon to render before `children`.
   */
  iconBefore?: IconName;
  /**
   * Indicates whether `Button` should be the full width of its container.
   */
  isFullWidth?: boolean;
  /**
   * Renders `Button` in a pill shaped format. Note that this
   * should only be used for `IconButton`s.
   */
  isPill?: boolean;
  /**
   * Indicates whether `Button` should render as a square,
   * making its height and width identical.
   */
  isSquare?: boolean;
  /**
   * Indicates whether `Button` should have a drop shadow.
   */
  hasShadow?: boolean;
  /**
   * Controls the size of `Button`, impacting its dimensions and typography.
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /**
   * Optional `id` attribute to apply to `Button` so that another node can reference it.
   */
  id?: string;
  /**
   * Indicates whether `Button` is for presentation purposes only, i.e. whether it is
   * interactive. When `true`, `Button` is rendered as a `span` rather than as a `Link`
   * or `button`.
   */
  presentationOnly?: boolean;
  /**
   * Function to call when `Button` is clicked.
   */
  onClick?: React.MouseEventHandler;
  /**
   * Function to call when a key is pressed on `Button`.
   */
  onKeyDown?: React.KeyboardEventHandler;
};

const Vars = createUniqueCustomProperties('Button', [
  'baseBackgroundColor',
  'baseBorderColor',
  'baseColor',
  'disabledBackgroundColor',
  'disabledBorderColor',
  'disabledColor',
  'disabledOutlineColor',
  'hoverBackgroundColor',
  'hoverBorderColor',
  'hoverColor',
  'hoverOutlineColor',
  'focusBackgroundColor',
  'focusBorderColor',
  'focusColor',
  'focusOutlineColor',
  'selectedBackgroundColor',
  'selectedBorderColor',
  'selectedColor',
  'selectedOutlineColor',
  'buttonContentPadding',
]);

const Styles = labelStyles('BaseButton', {
  root: css`
    // All of the CSS Custom Properties that are used by this
    // component, ensured to be unique. Most are color related
    // and are set below via '[data-color]' selectors.
    ${Vars.baseBackgroundColor}: transparent;
    ${Vars.baseBorderColor}: transparent;
    ${Vars.baseColor}: #000;
    ${Vars.disabledBackgroundColor}: ${Colors.gray10};
    ${Vars.disabledBorderColor}: transparent;
    ${Vars.disabledColor}: ${Colors.gray60};
    ${Vars.disabledOutlineColor}: transparent;
    ${Vars.hoverBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.hoverBorderColor}: var(${Vars.baseBorderColor});
    ${Vars.hoverColor}: var(${Vars.baseColor});
    ${Vars.hoverOutlineColor}: transparent;
    ${Vars.focusBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.focusBorderColor}: transparent;
    ${Vars.focusColor}: var(${Vars.baseColor});
    ${Vars.focusOutlineColor}: var(${Vars.focusBackgroundColor});
    ${Vars.selectedBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.selectedBorderColor}: transparent;
    ${Vars.selectedColor}: var(${Vars.baseColor});
    ${Vars.selectedOutlineColor}: transparent;
    ${Vars.buttonContentPadding}: 0 10px;

    background-color: var(${Vars.baseBackgroundColor});
    border-color: var(${Vars.baseBorderColor});
    color: var(${Vars.baseColor});

    align-items: center;
    appearance: none;
    border-radius: 10px;
    border-style: solid;
    border-width: 1px;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    // Safari applies 2px margin to buttons this clears it
    margin: 0;
    min-width: 0;
    opacity: 1;
    text-align: center;
    text-decoration: none;
    text-transform: none;
    transition: all 200ms ease-in-out;
    vertical-align: middle;
    white-space: nowrap;

    &::-moz-focus-inner {
      padding: 0;
      border: 0;
    }

    // This is the outline that renders around the button when
    // its focused. Its a pseudo element to preserve transparenc;
    // between the edge of the button and the edge of the outline.
    // Its defined here vs only in ':focus' so that it can transition
    // between states.
    &::before {
      content: '';
      position: absolute;
      pointer-events: none;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: 2px solid transparent;
      border-radius: 12px;
      transition-property: all;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: 200ms;
    }

    &:hover:not([disabled]),
    a:hover & {
      background-color: var(${Vars.hoverBackgroundColor});
      border-color: var(${Vars.hoverBorderColor});
      color: var(${Vars.hoverColor});
    }

    &:focus:not([disabled]),
    a:focus & {
      background-color: var(${Vars.focusBackgroundColor});
      border-color: var(${Vars.focusBorderColor});
      color: var(${Vars.focusColor});
      outline: none;
      &::before {
        border-color: var(${Vars.focusOutlineColor});
        top: -5px;
        right: -5px;
        bottom: -5px;
        left: -5px;
      }
    }

    &[data-selected] {
      background-color: var(${Vars.selectedBackgroundColor});
      border-color: var(${Vars.selectedBorderColor});
      color: var(${Vars.selectedColor});
      // Hide :focus outline on selected buttons
      &::before {
        content: none;
      }
    }

    &[data-color='primary'] {
      ${Vars.baseBackgroundColor}: ${Colors.ocean};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.ocean120};
    }

    &[data-color='primary-alt'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.gray80};
    }

    &[data-color='danger'] {
      ${Vars.baseBackgroundColor}: ${Colors.error};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.error120};
      ${Vars.hoverColor}: ${Colors.white};
      ${Vars.focusOutlineColor}: ${Colors.error};
      ${Vars.focusColor}: ${Colors.white};
    }

    &[data-color='secondary'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray10};
      ${Vars.baseColor}: ${Colors.gray};
      ${Vars.hoverBackgroundColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.white};
      ${Vars.focusBackgroundColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.white};
    }

    &[data-color='tertiary'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.ocean120};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};

      &[disabled] {
        background-color: transparent;
      }
    }

    &[data-color='icon-primary'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray10};
      ${Vars.baseColor}: ${Colors.gray};
      ${Vars.hoverColor}: ${Colors.ocean};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};
    }

    &[data-color='icon-alt'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.ocean120};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};

      &[disabled] {
        background-color: transparent;
      }
    }

    &[data-color='icon-secondary'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseBorderColor}: ${Colors.gray40};
      ${Vars.baseColor}: ${Colors.gray60};
      ${Vars.hoverColor}: ${Colors.gray};
      ${Vars.focusBorderColor}: ${Colors.ocean};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};
    }

    &[disabled] {
      cursor: not-allowed;
      background-color: var(${Vars.disabledBackgroundColor});
      border-color: var(${Vars.disabledBorderColor});
      color: var(${Vars.disabledColor});
      outline-color: var(${Vars.disabledOutlineColor});
    }

    &[data-full-width] {
      width: 100%;
    }

    &[data-shadowed] {
      box-shadow: ${Shadows.elevation5};
    }

    &[data-size='xsmall'] {
      ${Typography.smallPrint1Bold};
      ${Vars.buttonContentPadding}: 0 15px;
      height: 30px;
      border-radius: 7px;

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 8px 0 3px;
      }

      &[data-square] {
        width: 30px;
      }
    }

    &[data-size='small'] {
      ${Typography.smallPrint1Bold};
      ${Vars.buttonContentPadding}: 0 20px;
      height: 40px;

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 10px;
      }

      &[data-square] {
        width: 40px;
      }
    }

    &[data-size='medium'] {
      ${Typography.paragraph3Bold}
      ${Vars.buttonContentPadding}: 0 30px;
      height: 50px;

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 13px;
      }

      &[data-square] {
        width: 50px;
      }
    }

    &[data-size='large'] {
      ${Typography.paragraph3Bold}
      ${Vars.buttonContentPadding}: 0 30px;
      height: 60px;

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 18px;
      }

      &[data-square] {
        width: 60px;
      }
    }

    &[data-unpadded],
    &[data-square] {
      ${Vars.buttonContentPadding}: 0 !important;
    }

    &[data-pill] {
      border-radius: 100px !important;

      &::before {
        border-radius: 100px !important;
      }
    }
  `,
  buttonContent: css`
    display: flex;
    align-items: center;
    min-width: 0;
    padding: var(${Vars.buttonContentPadding});
  `,
  buttonText: css`
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,
  buttonIcon: css`
    display: block;
    flex: none;
    margin-right: 6px;
  `,
});

type ButtonIconProps = {
  iconName: IconName;
  size: number;
};

const ButtonIcon = (props: ButtonIconProps) => (
  <span css={Styles.buttonIcon}>
    <Icon
      canAcceptPointerEvents={false}
      isBlock={true}
      name={props.iconName}
      size={props.size}
    />
  </span>
);

const ButtonText: React.FC = (props) => (
  <span css={Styles.buttonText}>{props.children}</span>
);

const ButtonContent = (props: Props) => {
  return (
    <span css={Styles.buttonContent}>
      {props.iconBefore && <ButtonIcon iconName={props.iconBefore} size={24} />}
      <ButtonText>{props.children}</ButtonText>
    </span>
  );
};

const getTag = (props: Props) => {
  if (props.presentationOnly) {
    return 'span';
  }
  if (props.htmlFor && !props.disabled) {
    return 'label';
  }
  if (props.href && !props.disabled) {
    return Link;
  }
  return 'button';
};

const defaultProps = {
  disabled: false,
  isFullWidth: false,
  isPill: false,
};

/**
 * Base component for `Button` and `IconButton`. Exists to define a shared
 * interface while allowing components like `Button` and `IconButton` to
 * expose a subset of props.
 */
export function BaseButton(_props: Props) {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const ButtonTag = getTag(props);
  const isLink = ButtonTag === Link;
  // Components take `ariaLabel` while HTML elements take
  // `aria-label`; handle that discrepancy here. Worth a
  // possible refactor in the future to avoid this.
  const ariaProps = {
    [`${isLink ? 'ariaLabel' : 'aria-label'}`]: props.ariaLabel,
  };
  return (
    <ButtonTag
      aria-controls={props.ariaControls}
      aria-describedby={props.ariaDescribedBy}
      aria-disabled={props.disabled}
      aria-expanded={props.ariaExpanded}
      aria-haspopup={props.ariaHaspopup}
      aria-hidden={props.ariaHidden}
      {...ariaProps}
      // @ts-ignore TS doesn't understand Link[color=null]
      color={isLink ? null : undefined}
      css={Styles.root}
      data-color={props.color}
      data-icon={props.iconBefore ? 'before' : undefined}
      data-full-width={props.isFullWidth ? '' : undefined}
      data-pill={props.isPill ? '' : undefined}
      data-shadowed={props.hasShadow ? '' : undefined}
      data-size={props.size}
      data-square={props.isSquare ? '' : undefined}
      disabled={props.disabled}
      htmlFor={props.htmlFor}
      href={isLink ? props.href : undefined}
      id={props.id}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      rel={isLink ? props.rel : undefined}
      role={!isLink ? 'button' : undefined}
      tabIndex={props.tabIndex}
      target={isLink ? props.target : undefined}
      type={ButtonTag === 'button' ? props.type : undefined}
    >
      <ButtonContent {...props} />
    </ButtonTag>
  );
}
